<template>
	<div>
		<header class="flex items-center content-between gap-2">
			<h1 class="alpha heavy">Land tax calculator</h1>
			<ul>
				<li>
					<button class="button button--primary--small" @click.prevent="reset()">New Calculation</button>
				</li>
			</ul>
		</header>
		<div class="sections">
			<div class="section section--floating section--full">
				<v-tabs ref="tabs" :tabless="true">
					<v-tab id="calculation-form" label="Calculation Form">
						<form @submit.prevent="submit">
							<fieldset class="input-group">
								<label><span>Property price </span>
									<money v-model="form.data.propertyPrice"/>
									<div v-if="form.errors.get('propertyPrice')" class="error-block">
										<p v-html="form.errors.get('propertyPrice')"/>
									</div>
								</label>
								<label><span>Country </span>
									<select v-model="form.data.country">
										<option disabled selected value="">—</option>
										<option value="England">England</option>
										<option value="Wales">Wales</option>
									</select>
								</label>
								<label><span>Buying situation </span>
									<select v-model="form.data.buyingSituation">
										<option disabled selected value="">—</option>
										<option value="first">Buying first property</option>
										<option value="next">Buying next property</option>
										<option value="additional">Buying additional property</option>
									</select>
								</label>
							</fieldset>
							<ul class="actions">
								<li>
									<button class="button button--primary" @click.prevent="submit()">Calculate</button>
								</li>
							</ul>
						</form>
					</v-tab>
					<v-tab id="calculation" label="Calculation">
						<div v-if="calculation">
							<header>
								<h2
									v-if="calculation.taxExempt"
									class="beta">This purchase would be exempt from '{{ calculation.type }}'.</h2>
								<h2
									v-else
									class="beta">This purchase may be subject to a '{{ calculation.type }}' to the value of
									<nobr><span
										class="heavy"
										v-html="$present('price', calculation.tax)"/> <sup>1</sup></nobr>
								</h2>
							</header>
							<section class="mt-2">
								<header>
									<h3 class="beta heavy">How the tax was calculated</h3>
								</header>
								<dl class="mt-2">
									<dt>Property price</dt>
									<dd class="beta heavy" v-html="$present('price', form.data.propertyPrice)"/>
									<dt>Country</dt>
									<dd class="beta heavy" v-html="form.data.country"/>
									<dt>Buying situation</dt>
									<dd
										v-if="form.data.buyingSituation === 'first'"
										class="beta heavy">Buying first property
									</dd>
									<dd
										v-if="form.data.buyingSituation === 'next'"
										class="beta heavy">Buying next property
									</dd>
									<dd
										v-if="form.data.buyingSituation === 'additional'"
										class="beta heavy">Buying additional property
									</dd>
								</dl>
								<table class="mt-2">
									<thead>
										<tr>
											<th align="left">{{ stringer(calculation.rates).ucWords().join(" ").value }} property price thresholds</th>
											<th align="right">Portion</th>
											<th align="right">{{ calculation.typeAbbreviation }} rate</th>
											<th align="right">Amount</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="(rate, rateIndex) in calculation.breakdown"
											:key="rateIndex">
											<td v-if="!rate.lowerLimit">The portion up to and including {{ $present("price", rate.upperLimit) }}</td>
											<td v-else-if="!rate.upperLimit">The portion from {{ $present("price", rate.lowerLimit) }}</td>
											<td v-else>The portion from {{ $present("price", rate.lowerLimit) }} up to and including {{ $present("price", rate.upperLimit) }}</td>
											<td align="right" v-html="$present('price', rate.amountAtRate)"/>
											<td align="right" v-html="$present('percent', rate.rate)"/>
											<td align="right" v-html="$present('price', rate.tax)"/>
										</tr>
									</tbody>
								</table>
							</section>
							<div class="section section--flat mt-2">
								<div class="editorial">
									<h4 class="heavy">A Note About {{ calculation.type }}</h4>
									<p v-if="calculation.type === 'Stamp Duty Land Tax'">Stamp Duty Land Tax (SDLT) is payable on property purchases. The amount payable is calculated against the agreed purchase price. Please note that, as of April 1st 2016, a higher rate of SDLT is payable on second properties, such as Buy-to-Let houses, or holiday homes.</p>
									<p v-if="calculation.type === 'Land Transaction Tax'">Land Transaction Tax (LTT) is payable on property purchases. The amount payable is calculated against the agreed purchase price. A higher rate of LTT is payable on second properties, such as Buy-to-Let houses, or holiday homes.</p>
									<ul>
										<li v-if="calculation.type === 'Stamp Duty Land Tax'">
											<a
												href="https://www.gov.uk/stamp-duty-land-tax"
												target="_blank">Further information on Stamp Duty Land Tax (England/Northern Ireland)</a>
										</li>
										<li v-if="calculation.type === 'Land Transaction Tax'">
											<a
												href="https://www.gov.wales/land-transaction-tax-guide"
												target="_blank">Further information on Land Transaction Tax (Wales)</a>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</v-tab>
				</v-tabs>
			</div>
		</div>
	</div>
</template>
<script>
import AddressLookup from "@/components/forms/fields/AddressLookup.vue";

export default {
	components: {
		AddressLookup,
	},

	data() {
		return {
			form: this.newForm({
				country: "",
				propertyPrice: "",
				buyingSituation: "",
			}),
			calculation: null,
		};
	},

	computed: {
		propertyAddressIsOk() {
			return this.form.data.address?.address_country !== "Scotland";
		},
	},

	methods: {
		submit() {
			this.form.post("tools/land-tax-calculation").then((data) => {
				this.calculation = data;
				this.refs.tabs.goToTab("calculation");
			});
		},
		reset() {
			this.calculation = null;
			this.form.reset();
			this.refs.tabs.goToTab("calculation-form");
		},
	},
};
</script>
